import { atMinWidth, typeStyle, units } from '@m/alchemy-ui/theme';
import { useIntl } from 'react-intl';

import { Button } from '@m/alchemy-ui/Button';
import { Field } from '@m/alchemy-ui/Field';
import { Radio } from '@m/alchemy-ui/Radio';
import { RadioGroup } from '@m/alchemy-ui/RadioGroup';
import { Select } from '@m/alchemy-ui/Select';

import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { useState } from 'react';
import { type GetChallengesForSearchQuery, type GetChallengesForSearchQueryVariables } from '../../../graphql/queries';
import { CHALLENGES_FOR_SEARCH_QUERY } from '../../../graphql/queries/challenge';
import { useRuntimeConfig } from '../../../context';
import { Breakpoints } from '../../../common/enums';
import { PEOPLE_SEARCH_CHARACTER_MIN } from '../../shared-constants';
import * as SearchStyled from './SearchForm.styled';

enum SearchTarget {
  IDEAS = 'ideas',
  PEOPLE = 'people'
}

const PeopleForm = () => {
  const intl = useIntl();
  const [peopleSearch, setPeopleSearch] = useState<string>('');

  const handleClearPeopleSearch = () => {
    setPeopleSearch('');
  };

  return (
    <form action="/user/search" method="get" data-testid="people-form">
      <Field
        isFullWidth
        label={intl.formatMessage({
          id: 'Search for Users',
          defaultMessage: 'Search for Users'
        })}
        input={
          <SearchStyled.SearchInputWrapper>
            <SearchStyled.SearchInput
              value={peopleSearch}
              onChange={(e) => setPeopleSearch(e.target.value)}
              placeholder={intl.formatMessage({ id: 'Search for a user', defaultMessage: 'Search for a user' })}
              aria-label={intl.formatMessage({ id: 'Search for a user', defaultMessage: 'Search for a user' })}
              type="search"
              name="q"
              minLength={PEOPLE_SEARCH_CHARACTER_MIN}
              required
            />
            {peopleSearch && (
              <SearchStyled.ClearButton
                type="button"
                aria-label={intl.formatMessage({
                  id: 'Clear Search for Users field',
                  defaultMessage: 'Clear Search for Users field'
                })}
                onClick={handleClearPeopleSearch}
              />
            )}
          </SearchStyled.SearchInputWrapper>
        }
      />
      <div>
        <Button type="submit">{intl.formatMessage({ id: 'Search', defaultMessage: 'Search' })}</Button>
      </div>
    </form>
  );
};

const ALL_CHALLENGES = '0';

const ChallengeSelector = ({
  value,
  onChange
}: {
  readonly value: string;
  readonly onChange: (challengeId: string) => void;
}) => {
  const intl = useIntl();
  const ALL_CHALLENGES_OPTION = {
    value: ALL_CHALLENGES,
    label: intl.formatMessage({ id: 'All Challenges', defaultMessage: 'All Challenges' })
  };
  const { data, loading } = useQuery<GetChallengesForSearchQuery, GetChallengesForSearchQueryVariables>(
    CHALLENGES_FOR_SEARCH_QUERY
  );
  const options = [
    ALL_CHALLENGES_OPTION,
    ...(data?.challenges || []).map(({ id, title }) => ({ value: id, label: title }))
  ];
  return (
    <Field
      isFullWidth
      label={intl.formatMessage({ id: 'Search by challenge', defaultMessage: 'Search by challenge' })}
      input={
        <Select
          value={value}
          onChange={(option) => onChange(option.value)}
          options={options}
          isLoading={loading}
          isSearchable={options.length > 10}
          // Remove empty <option> elements with aria-live="off" to improve accessibility.
          // This is a temporary fix and can be removed after the Alchemy update.
          onMenuOpen={() => {
            document.querySelectorAll('li[aria-live="off"]').forEach((li) => {
              if (!li.textContent?.trim()) {
                li.remove();
              }
            });
          }}
        />
      }
    />
  );
};

const StyledField = styled(Field)`
  & button {
    margin-bottom: ${units(0.5)};

    &:not(:last-child) {
      margin-right: ${units(1)};
    }
  }
`;

const TagList = ({ onTagClick, search }: { readonly onTagClick: (tag: string) => void; readonly search: string }) => {
  const intl = useIntl();
  const { popularTags: tags } = useRuntimeConfig();
  const [announcement, setAnnouncement] = useState('');

  if (tags.length === 0) {
    return null;
  }

  const handleClick = (tag: string) => {
    const alreadySelected = isTagInSearch(tag);

    onTagClick(tag);

    setAnnouncement(
      alreadySelected
        ? intl.formatMessage({ id: 'tagRemovedAnnouncement', defaultMessage: '{tag} removed from search' }, { tag })
        : intl.formatMessage({ id: 'tagAddedAnnouncement', defaultMessage: '{tag} added to search' }, { tag })
    );
  };

  const isTagInSearch = (tag: string) => search.includes(`[${tag}]`);

  return (
    <StyledField
      label={intl.formatMessage({ id: 'Popular Tags', defaultMessage: 'Popular Tags' })}
      input={
        <>
          {tags.map((tag) => {
            const selected = isTagInSearch(tag);

            return (
              <Button
                key={tag}
                priority={selected ? 'primary' : 'secondary'}
                type="button"
                size="small"
                aria-pressed={selected}
                aria-label={intl.formatMessage(
                  { id: 'SearchTagLabel', defaultMessage: 'Add tag {tag} to the search text' },
                  { tag }
                )}
                onClick={() => handleClick(tag)}
              >
                {tag}
              </Button>
            );
          })}
          <SearchStyled.LiveRegion aria-live="polite" role="status">
            {announcement}
          </SearchStyled.LiveRegion>
        </>
      }
    />
  );
};

const IdeasForm = () => {
  const intl = useIntl();
  const [challenge, setChallenge] = useState<string>(ALL_CHALLENGES);
  const [search, setSearch] = useState<string>('');

  const handleTagClick = (tag: string) => {
    if (search.includes(`[${tag}]`)) {
      setSearch(search.replace(`[${tag}]`, ''));
    } else {
      setSearch(`${search}[${tag}]`);
    }
  };
  const handleClearSearch = () => {
    setSearch('');
  };
  return (
    <form action={`category/${challenge}`} method="get" data-testid="ideas-form">
      <Field
        isFullWidth
        label={intl.formatMessage({
          id: 'Search for Ideas',
          defaultMessage: 'Search for Ideas'
        })}
        input={
          <SearchStyled.SearchInputWrapper>
            <SearchStyled.SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'Search Crowdicity: use [tag name] to search tags',
                defaultMessage: 'Search Crowdicity: use [tag name] to search tags'
              })}
              aria-label={intl.formatMessage({
                id: 'Search Crowdicity: use [tag name] to search tags',
                defaultMessage: 'Search Crowdicity: use [tag name] to search tags'
              })}
              type="search"
              name="q"
            />
            {search && (
              <SearchStyled.ClearButton
                type="button"
                aria-label={intl.formatMessage({
                  id: 'Clear Search for Ideas field',
                  defaultMessage: 'Clear Search for Ideas field'
                })}
                onClick={handleClearSearch}
              />
            )}
          </SearchStyled.SearchInputWrapper>
        }
      />
      <ChallengeSelector value={challenge} onChange={(challengeId) => setChallenge(challengeId)} />
      <TagList onTagClick={handleTagClick} search={search} />
      <div>
        <Button type="submit">{intl.formatMessage({ id: 'Search', defaultMessage: 'Search' })}</Button>
      </div>
    </form>
  );
};

const FormWrapper = styled.div`
  margin: ${units(1)};
  max-height: 50vh;
  overflow: auto;
  width: 80vw;
  padding: ${units(1)};

  ${atMinWidth(
    Breakpoints.TABLET_MIN,
    `
      max-height: unset;
      overflow: unset;
      width: 600px;
    `
  )};

  & form > :last-child {
    text-align: right;
  }

  & form > div:not(:last-child) {
    margin-bottom: ${units(3)};
  }
`;

export const Header = styled.h1`
  ${typeStyle('headerL')};
  margin-bottom: ${units(2)};
`;

export const SearchForm = () => {
  const intl = useIntl();
  const [searchTarget, setSearchTarget] = useState<SearchTarget>(SearchTarget.IDEAS);
  const { isPeopleSearchEnabled } = useRuntimeConfig();
  return (
    <FormWrapper>
      <Header data-testid="form-header">{intl.formatMessage({ id: 'Search', defaultMessage: 'Search' })}</Header>
      {isPeopleSearchEnabled && (
        <>
          <Field
            label={intl.formatMessage({ id: 'Search in:', defaultMessage: 'Search in:' })}
            input={
              <RadioGroup
                layout="row"
                selection={searchTarget}
                onChange={(selection: SearchTarget) => setSearchTarget(selection)}
              >
                {({ isSelected, handleChange }) => [
                  <Radio
                    key={SearchTarget.IDEAS}
                    name="search-target"
                    checked={isSelected(SearchTarget.IDEAS)}
                    value={SearchTarget.IDEAS}
                    label={intl.formatMessage({ id: 'Ideas', defaultMessage: 'Ideas' })}
                    onChange={() => handleChange(SearchTarget.IDEAS)}
                  />,
                  <Radio
                    key={SearchTarget.PEOPLE}
                    checked={isSelected(SearchTarget.PEOPLE)}
                    name="search-target"
                    value={SearchTarget.PEOPLE}
                    label={intl.formatMessage({ id: 'People', defaultMessage: 'People' })}
                    onChange={() => handleChange(SearchTarget.PEOPLE)}
                  />
                ]}
              </RadioGroup>
            }
          />
          <div style={{ display: searchTarget === SearchTarget.PEOPLE ? 'block' : 'none' }}>
            <PeopleForm />
          </div>
        </>
      )}
      <div style={{ display: searchTarget === SearchTarget.IDEAS ? 'block' : 'none' }}>
        <IdeasForm />
      </div>
    </FormWrapper>
  );
};
